import type { DeliveryReconOverviewRow } from './types'
import type { DateRangeFilterResult } from '@/hooks/useDateRangeFilter'
import type { Filter } from '@gravitate-js/excalibrr/dist/components/Grid/SearchGridHeader/DynamicFilterForm/types'

import dayjs from 'dayjs'
import { uniqBy } from 'lodash'

export const REPORT_ACTIONS = ['Match', 'Approve', 'Unmatch', 'Void', 'History'] as const
export const DETAILS_ACTIONS = ['Unlink'] as const
export const CHANGE_BAR_ACTIONS = [...REPORT_ACTIONS.filter((a) => a !== 'History')] as const
export const ACTIONS = [...REPORT_ACTIONS, ...DETAILS_ACTIONS] as const

// The swing report Deliveries List drawer provides a link to the Delivery Report
// We need to initialize the filters from state passed from the swing report (site, date)
export function setFiltersFromSwingReport(
  state: { date: string[]; site: string[] },
  setDayRange: DateRangeFilterResult['setDayRange'],
  setFilters: (filters: Filter) => void
) {
  const from = state.date?.[0]
  const to = state.date?.[1] ?? state.date?.[0]
  setDayRange({
    From: dayjs(from).startOf('day'),
    To: dayjs(to).endOf('day'),
  })
  setFilters({
    sites: state.site,
  })
}

export function getTotalVolume(details: DeliveryReconOverviewRow['details'], type: 'bol' | 'delivery') {
  return parseInt(
    details
      .filter((d) => d.type === type)
      .reduce((acc, curr) => acc + (curr.volume || 0), 0)
      .toFixed(0)
  )
}

export function getTotalVariance(details: DeliveryReconOverviewRow['details']) {
  const bolVolume = getTotalVolume(details, 'bol')
  const deliveryVolume = getTotalVolume(details, 'delivery')
  return Math.abs(bolVolume - deliveryVolume)
}

export function getTimeVariance(details: DeliveryReconOverviewRow['details'] | undefined) {
  if (!details?.length) return 0
  const dates = details.map((d) => d.date).map((d) => dayjs(d))
  return dayjs.max(dates)?.diff(dayjs.min(dates), 'minute')
}

export function formatDuration(minutes: number | null | undefined) {
  if (!minutes) return '0:00'
  return `${Math.floor(minutes / 60)}:${(minutes % 60).toString().padStart(2, '0')}`
}

export function getFormattedTimeVariance(details: DeliveryReconOverviewRow['details']) {
  const minutes = getTimeVariance(details)
  return formatDuration(minutes)
}

export function getOrderNumberForPo(
  details: DeliveryReconOverviewRow['details'] | undefined,
  po: DeliveryReconOverviewRow['po_numbers'][number]
) {
  return details?.find((d) => d.purchase_order === po)?.order_number
}

export function getShouldDisableActionsDropdown(selected: DeliveryReconOverviewRow[]): Record<string, boolean> {
  const hasApproved = selected.some((d) => d.status === 'approved')
  return {
    Match: selected.length <= 1 || hasApproved,
    Approve: selected.some((d) => d.details.every((d) => d.type === 'delivery')),
    Unmatch:
      selected.some((d) => uniqBy(d.details, (item) => item.source_identifier?.[0].number).length <= 1) || hasApproved,
    Void:
      selected.some((s) => s.details.length > 1) ||
      !selected.every((d) => d.details.every((d) => d.type === 'delivery')),
  }
}
